class Hint extends HTMLElement {
	#elements;

	/**
	 * Set up hint custom element
	 */
	constructor() {
		super();

		this.#elements = {
			button: /** @type {HTMLButtonElement} */ (
				this.querySelector(".Hint-header")
			),
			content: /** @type {HTMLDivElement} */ (
				this.querySelector(".Hint-content")
			),
		};

		this.#addEventListeners();
	}

	/**
	 * Add event listeners
	 */
	#addEventListeners() {
		if (this.#elements.button) {
			this.#elements.button.addEventListener(
				"click",
				this.#onToggle.bind(this),
			);
		}
	}

	/**
	 * Handle toggle event
	 * @param {Event} event
	 */
	#onToggle(event) {
		event.preventDefault();

		// Check if this hint is in the active/centered slide
		const parentSlide = this.closest(".Hints-slide");
		const isActive = parentSlide?.classList.contains("Hints-slide--active");

		// If not active/centered, navigate to center this hint instead of expanding
		if (!isActive) {
			this.#navigateToCenter();
			return;
		}

		// If active/centered, toggle expansion normally
		const isExpanded = this.classList.contains("is-expanded");

		if (isExpanded) {
			this.#collapse();
		} else {
			this.#expand();
		}
	}

	/**
	 * Navigate this hint to center position
	 * Closes any expanded hints, triggers carousel navigation, then expands this hint
	 */
	#navigateToCenter() {
		// Find the parent Hints component
		const hintsComponent = this.closest("cd-hints");
		if (!hintsComponent) return;

		// Close all expanded hints before navigation
		const allHints = hintsComponent.querySelectorAll("cd-hint.is-expanded");
		allHints.forEach((hint) => {
			hint.classList.remove("is-expanded");
			const button = hint.querySelector(".Hint-header");
			if (button) {
				button.setAttribute("aria-expanded", "false");
			}
		});

		// Find which slide this hint is in
		const parentSlide = this.closest(".Hints-slide");
		if (!parentSlide) return;

		// Get all slides to determine the index
		const allSlides = hintsComponent.querySelectorAll(".Hints-slide");
		const slideIndex = Array.from(allSlides).indexOf(parentSlide);

		if (slideIndex === -1) return;

		// Navigate to the page (1-indexed)
		const targetPage = slideIndex + 1;

		// Call the goToPage method on the Hints component
		if (typeof hintsComponent.getCurrentPage === "function") {
			hintsComponent.dispatchEvent(
				new CustomEvent("hints:navigate", {
					detail: { targetPage },
					bubbles: true,
				}),
			);

			// Directly trigger navigation using the internal method
			// We need to access the private method, so we'll dispatch a pager click event
			const pagerButtons = hintsComponent.querySelectorAll(".Pager-button");
			const currentPage = hintsComponent.getCurrentPage();

			// Calculate how many clicks needed
			const diff = targetPage - currentPage;
			if (diff === 0) return;

			// Click prev/next button the appropriate number of times
			const button = diff > 0 ? pagerButtons[1] : pagerButtons[0];
			if (button) {
				for (let i = 0; i < Math.abs(diff); i++) {
					button.click();
				}
			}

			// After navigation completes, expand this hint
			// Wait for the transition to finish
			const transitionDuration = 400; // Match the CSS transition duration
			setTimeout(() => {
				this.#expand();
			}, transitionDuration);
		}
	}

	/**
	 * Expand the hint
	 */
	#expand() {
		this.classList.add("is-expanded");
		this.#elements.button.setAttribute("aria-expanded", "true");
	}

	/**
	 * Collapse the hint
	 */
	#collapse() {
		this.classList.remove("is-expanded");
		this.#elements.button.setAttribute("aria-expanded", "false");
	}
}

customElements.define("cd-hint", Hint);
